<template>
  <CModal :show.sync="show" centered size="xl">
    <PContextMenu ref="contextMenu" :menu-items="contextItems" @item-selected="onContextMenuSelected"/>
    <template #header>
      <div>
        <div class="d-flex">
          <div class="align-self-center">
            <h5 class="modal-title">
              {{ data.fileName }}
              <span v-if="permissions.canSeeId && data.photo" class="ml-2">
                [ ID {{ data.photo.id }} ]
              </span>
            </h5>
          </div>
        </div>
      </div>
      <CButtonClose @click="close"/>
    </template>
    <div class="d-flex">
      <!-- Left side - File information -->
      <div class="file-info-column">
        <div class="file-info-section">
          <h6 class="mb-3">File Information</h6>
          <div class="file-info-item">
            <div class="file-info-label">File Name:</div>
            <div class="file-info-value">{{ data.fileName || 'N/A' }}</div>
          </div>
          <div class="file-info-item">
            <div class="file-info-label">Shot Date:</div>
            <div class="file-info-value">{{ data.shot_at || 'N/A' }}</div>
          </div>
          <div class="file-info-item">
            <div class="file-info-label">Creation Date:</div>
            <div class="file-info-value">{{ data.created_at || 'N/A' }}</div>
          </div>
          <div class="file-info-item">
            <div class="file-info-label">Size:</div>
            <div class="file-info-value">{{ data.size || 'N/A' }}</div>
          </div>
          <div class="file-info-item">
            <div class="file-info-label">Resolution:</div>
            <div class="file-info-value">{{ data.resolution || 'N/A' }}</div>
          </div>
        </div>

        <div class="tags-section" v-if="selectedFace">
          <h3>Tags</h3>
          <div class="tags-container">
            <div v-for="(tag, tagIndex) in selectedFace.tags" :key="tagIndex" class="tag-item">
              {{ tag }}
              <span 
                v-if="permissions.canUpdateFaceTags"
                class="tag-remove" 
                @click="removeTag(selectedFace.id, tagIndex)"
              >×</span>
            </div>
            <div 
              v-if="permissions.canUpdateFaceTags"
              class="tag-input-container"
            >
              <input 
                type="text" 
                class="tag-input" 
                placeholder="Nuovo tag..."
                @keydown="handleTagInputKeydown($event, selectedFace.id)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Middle - Image with person squares -->
      <div class="image-container position-relative">
        <transition :name="transitionName" mode="out-in">
          <div class="image-wrapper" v-if="showImage">
            <img class="modal-preview-image" :src="data.src" alt="" ref="imageRef"/>
            <!-- Darkening overlay that appears when a face is selected -->
            <div
                class="darkening-overlay"
                v-if="selectedFaceId !== null && detail && detail.faces && permissions.canSeeFaces"
                :style="selectedFaceClipPathStyle"
            ></div>
            <!-- Person squares overlay -->
            <div class="person-squares-overlay" v-if="detail && detail.faces && permissions.canSeeFaces">
              <div
                  v-for="face in detail.faces"
                  :key="face.id"
                  class="person-square"
                  :style="{
                  left: face.x + '%',
                  top: face.y + '%',
                  width: face.width + '%',
                  height: face.height + '%'
                }"
                  :class="{ 'highlighted': highlightedPersonId === face.id, 'selected': selectedFaceId === face.id }"
                  @mouseover="highlightPerson(face.id)"
                  @mouseleave="clearHighlight"
                  @click="selectFace(face.id)"
                  @contextmenu.prevent="openContextMenuForSquare($event, face.id)"
              ></div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Right side - People list -->
      <div class="people-list" v-if="detail && detail.faces && permissions.canSeeFaces">
        <h6 class="mb-3">People in this photo</h6>
        <div class="people-container">
          <div
              v-for="face in detail.faces"
              :key="face.id"
              class="person-item d-flex align-items-center justify-content-between mb-3"
              :class="{ 'highlighted': highlightedPersonId === face.id, 'selected': selectedFaceId === face.id }"
              @mouseover="highlightPerson(face.id)"
              @mouseleave="clearHighlight"
              @click="selectFace(face.id)"
          >
            <div class="d-flex align-items-center">
              <div class="person-avatar">
                <img :src="face.avatar" alt="" class="rounded-circle"/>
              </div>
              <div class="person-name ml-2">
                {{ face.name }}
              </div>
            </div>
            <div class="menu-dots" @click.stop="openContextMenuForProfile($event, face.id)">
              <CIcon name="cipEllipsisVertical" size="custom-size" :width="16"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CLink @click="onChange('prev')" class="nav-photo nav-photo--prev" :disabled="!data.has_prev">
      <CIcon name="cipChevronLeft" size="custom-size" :width="36"/>
    </CLink>
    <CLink @click="onChange('next')" class="nav-photo nav-photo--next" :disabled="!data.has_next">
      <CIcon name="cipChevronRight" size="custom-size" :width="36"/>
    </CLink>
    <template #footer>
      <div class="d-flex justify-content-between align-items-end w-100">
        <div class="d-flex">
          <CIcon name="cipPin" size="custom-size" :width="22" class="icon-filled text-primary mr-2"
                 v-if="data.is_public"/>
          <div>
            <div style="font-size: 12px">{{ data.shot_at }}</div>
            <div v-if="data.photo && !data.photo.processed_at">
              <CBadge color="light" class="badge-outline mt-2">
                <CIcon name="cipDot" size="custom-size" :height="8" class="mr-2 text-warning"
                       style="vertical-align: baseline"/>
                <span v-translate>Photo not delivered</span>
              </CBadge>
            </div>
            <div v-if="data.photo && data.photo.soft_deleted">
              <CBadge color="light" class="badge-outline mt-2">
                <CIcon name="cipEyeOff" size="custom-size" :height="14" class="mr-2" style="vertical-align: middle"/>
                <span v-translate>Photo hidden</span>
              </CBadge>
            </div>
            <div v-if="requireDetailInfo" class="mt-2" style="min-height: 30px">
              <template v-if="detail">
                <CBadge color="light" class="badge-outline mr-2"
                        v-if="permissions.canSeeDelivered && permissions.canSeeDeliverable">
                  <CIcon name="cipDot" size="custom-size" :height="8" style="vertical-align: baseline"
                         :class="['mr-2', detail.num_delivered === detail.faces_count ? 'text-success' : 'text-warning']"/>
                  <translate>Photos delivered:</translate>
                  {{ detail.num_delivered }}
                  <translate>of</translate>
                  {{ detail.faces_count }}
                </CBadge>
                <template v-else>
                  <CBadge color="light" class="badge-outline mr-2" v-if="permissions.canSeeDelivered">
                    <CIcon name="cipDot" size="custom-size" :height="8" class="mr-2 text-success"
                           style="vertical-align: baseline"/>
                    <span v-translate>Delivered: </span>
                    <span>{{ detail.num_delivered }}</span>
                  </CBadge>
                  <CBadge color="light" class="badge-outline mr-2" v-if="permissions.canSeeDeliverable">
                    <CIcon name="cipDot" size="custom-size" :height="8" class="mr-2 text-warning"
                           style="vertical-align: baseline"/>
                    <span v-translate>Deliverable: </span>
                    <span>{{ detail.faces_count }}</span>
                  </CBadge>
                </template>
                <CBadge color="light" class="badge-outline mr-2" v-if="permissions.canSeePhotographer && detail.photographer_email">
                  <CIcon name="cipPhotographer" size="custom-size" :height="14" class="mr-2 text-info"
                         style="vertical-align: baseline"/>
                  <span>{{ detail.photographer_email }}</span>
                </CBadge>
              </template>
              <CIcon name="cipCircleLoader" size="custom-size" :width="16" class="rotating" v-else/>
            </div>
          </div>
        </div>
        <div class="d-flex">
          <CButton @click="show = false" color="secondary" class="mr-2" v-translate>Close</CButton>
          <CButton @click="downloadSingle(data.photo)" color="primary" v-if="permissions.canDownload">
            Download
            <CIcon name="cipDownload" size="custom-size" :width="16" class="ml-1"/>
          </CButton>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import detail from "@/domain/eventWizard/components/folderMapping/Detail.vue";
import PContextMenu from "@/domain/core/components/PContextMenu.vue";

export default {
  name: "DetailedImage",
  components: {
    PContextMenu
  },
  emits: ['download', 'change'],
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    detail() {
      return detail
    },
    ...mapGetters('photoSearch', [
      'permissions',
      'detail'
    ]),
    requireDetailInfo() {
      return this.permissions.canSeeDelivered
          || this.permissions.canSeeDeliverable
          || this.permissions.canSeeCheckin
          || this.permissions.canSeeFaces
          || this.permissions.canSeePhotographer
    },
    selectedFace() {
      if (!this.detail || !this.detail.faces) return null
      return this.selectedFaceId !== null ? this.detail.faces.find(face => face.id === this.selectedFaceId) : null
    },
    selectedFaceClipPathStyle() {
      if (this.selectedFaceId === null) return {}

      const face = this.selectedFace
      if (!face) return {}

      // Create a polygon that excludes the selected face area
      const x = face.x
      const y = face.y
      const width = face.width
      const height = face.height
      return {
        clipPath: `polygon(
          ${x + width}% ${y}%,
          100% 0%, 
          100% 100%, 
          0% 100%, 
          0% 0%, 
          100% 0%,
          ${x + width}% ${y}%,
          ${x}% ${y}%,
          ${x}% ${y + height}%,
          ${x + width}% ${y + height}%
        )`
      }
    },
    contextItems() {
      if (this.contextType === 'square') {
        // Menu items for squares in the main image
        const actions = []
        
        if (this.permissions.canDeleteFace) {
          actions.push({
            value: 'delete_face',
            label: this.$pgettext('detailed_image.action', 'Elimina volto'),
            icon: 'cipTrashFull'
          })
        }
        
        actions.push({
          value: 'assign_name',
          label: this.$pgettext('detailed_image.action', 'Assegna nome'),
          icon: 'cipPencilLine'
        })
        
        actions.push({
          value: 'add_tag',
          label: this.$pgettext('detailed_image.action', 'Aggiungi tag'),
          icon: 'cipTagPlus'
        })
        
        return actions
      } else if (this.contextType === 'profile') {
        // Menu items for profiles in the right column
        const actions = []
        
        if (this.permissions.canDeleteFace) {
          actions.push({
            value: 'delete_face',
            label: this.$pgettext('detailed_image.action', 'Elimina volto'),
            icon: 'cipTrashFull'
          })
        }
        
        actions.push({
          value: 'highlight',
          label: this.$pgettext('detailed_image.action', 'Evidenzia'),
          icon: 'cipHighlight'
        })
        
        actions.push({
          value: 'add_tag',
          label: this.$pgettext('detailed_image.action', 'Aggiungi tag'),
          icon: 'cipTagPlus'
        })
        
        return actions
      }
      return []
    }
  },
  data() {
    return {
      show: false,
      showImage: true,
      transitionName: '',
      highlightedPersonId: null,
      contextType: null, // 'square' or 'profile'
      selectedPersonId: null, // ID of the person selected for context menu
      selectedFaceId: null, // ID of the selected face for displaying tags
      newTagInputs: {} // Per memorizzare i valori degli input per ogni face
    }
  },
  methods: {
    ...mapActions('photoSearch', [
      'fetchMediaDetail',
      'updateFaceTags',
      'deleteFace'
    ]),
    open() {
      this.show = true
      document.addEventListener('keydown', this.handleKeydown)
      this.onOpenImage()
    },
    close() {
      this.show = false
      document.removeEventListener('keydown', this.handleKeydown)
    },
    downloadSingle(photo) {
      this.$emit('download', photo)
    },
    async onChange(direction) {
      this.transitionName = direction === 'prev' ? 'slide-horizontal-left' : 'slide-horizontal-right'
      await this.$nextTick()
      this.showImage = false
      await this.$nextTick()
      await this.delay(200)
      this.showImage = true
      this.$emit('change', direction)
      await this.onOpenImage()
    },
    handleKeydown(event) {
      if (event.key === 'ArrowLeft' && this.data.has_prev) {
        this.onChange('prev')
      } else if (event.key === 'ArrowRight' && this.data.has_next) {
        this.onChange('next')
      }
    },
    async onOpenImage() {
      if (!this.requireDetailInfo) return
      await this.fetchMediaDetail(this.data.photo.id)
    },
    // New methods for person highlighting
    highlightPerson(id) {
      this.highlightedPersonId = id
    },
    clearHighlight() {
      this.highlightedPersonId = null
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    // Context menu methods
    openContextMenuForSquare(event, id) {
      this.contextType = 'square'
      this.selectedPersonId = id
      // Automatically select the face when right-clicking
      this.selectedFaceId = id
      this.$refs.contextMenu.open(event)
    },

    openContextMenuForProfile(event, id) {
      this.contextType = 'profile'
      this.selectedPersonId = id
      // Automatically select the face when clicking on menu dots
      this.selectedFaceId = id
      this.$refs.contextMenu.open(event)
    },

    selectFace(id) {
      // Toggle selection: if the face is already selected, deselect it
      if (this.selectedFaceId === id) {
        this.selectedFaceId = null
      } else {
        this.selectedFaceId = id
      }
    },

    async removeTag(faceId, tagIndex) {
      const face = this.detail.faces.find(f => f.id === faceId)
      if (face && face.tags) {
        // Rimuoviamo il tag dall'array
        const updatedTags = [...face.tags]
        updatedTags.splice(tagIndex, 1)
        
        // Chiamiamo l'azione per aggiornare i tag sul server
        await this.updateFaceTags({
          face_id: faceId,
          tags: updatedTags
        })
      }
    },

    async addTagToPerson(faceId, tag) {
      const face = this.detail.faces.find(f => f.id === faceId)
      if (face) {
        if (!face.tags) {
          this.$set(face, 'tags', [])
        }
        const updatedTags = [...face.tags, tag]
        await this.updateFaceTags({
          face_id: faceId,
          tags: updatedTags
        })
      }
    },

    async onContextMenuSelected(action) {
      if (!this.selectedFace) return

      const face = this.selectedFace

      if (action === 'delete_face') {
        if (this.permissions.canDeleteFace) {
          await this.deleteFace(face.id)
        }
      } else if (action === 'assign_name') {
        // For now, just log the action
        console.log('Assign name for face at', {x: face.x, y: face.y, width: face.width, height: face.height})
        // In a real implementation, you would open a dialog to enter a name
      } else if (action === 'highlight') {
        // For now, just highlight the person
        this.highlightPerson(this.selectedPersonId)
        // In a real implementation, you might do something more sophisticated
      } else if (action === 'add_tag') {
        // For now, just log the action and add a mock tag
        console.log('Add tag for', face.name)
        // In a real implementation, you would open a dialog to enter a tag
      }

      // Update selected face if it's the same as the one we just modified
      if (this.selectedFaceId === this.selectedPersonId) {
        this.selectFace(this.selectedPersonId)
      }
    },

    handleTagInputKeydown(event, faceId) {
      if (event.key === 'Enter' && event.target.value.trim()) {
        this.addTagToPerson(faceId, event.target.value.trim())
        event.target.value = ''
      }
    }
  }
}
</script>

<style scoped>
/* Layout containers */
.image-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  max-height: 70vh;
  margin: 0 15px;
}

.image-wrapper {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.modal-preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

/* File info column */
.file-info-column {
  width: 250px;
  padding: 0 15px;
  border-right: 1px solid #eee;
  max-height: 70vh;
  overflow-y: auto;
}

.file-info-section {
  margin-bottom: 20px;
}

.file-info-item {
  margin-bottom: 8px;
  font-size: 14px;
}

.file-info-label {
  font-weight: bold;
  color: #6c757d;
}

.file-info-value {
  margin-top: 2px;
}

/* Tags section */
.tags-section {
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.tag-item {
  background-color: #f0f0f0;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  position: relative;
}

.tag-remove {
  margin-left: 6px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.tag-item:hover .tag-remove {
  opacity: 1;
}

/* People list */
.people-list {
  width: 250px;
  padding: 0 15px;
  border-left: 1px solid #eee;
  max-height: 70vh;
  overflow-y: auto;
}

.person-squares-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2; /* Above the darkening overlay */
}

/* Darkening overlay styles */
.darkening-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3); /* Lighter semi-transparent black */
  z-index: 1; /* Above the image but below the person squares */
  pointer-events: none; /* Allow clicks to pass through to elements below */
}

.person-square {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.1);
  pointer-events: auto;
  transition: all 0.2s ease;
  cursor: pointer;
}

.person-square.highlighted {
  border-color: rgba(0, 123, 255, 0.9);
  background-color: rgba(0, 123, 255, 0.2);
}

.person-square.selected {
  border-color: rgba(255, 255, 255, 1);
  box-shadow: 0 0 10px 3px rgba(255, 255, 255, 0.7);
  z-index: 3; /* Ensure selected face is above all other elements */
}

.person-item {
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.person-item.highlighted {
  background-color: rgba(0, 123, 255, 0.1);
}

.person-item.selected {
  background-color: rgba(220, 53, 69, 0.1);
  border-left: 3px solid rgba(220, 53, 69, 0.8);
}

.person-avatar {
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
}

.person-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.menu-dots {
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.menu-dots:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Navigation */
.nav-photo {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
}

.nav-photo--prev {
  left: 270px; /* Adjusted to account for the file info column */
}

.nav-photo--next {
  right: 260px; /* Adjusted to account for the people list */
}

/* Transitions */
.slide-horizontal-left-enter-active,
.slide-horizontal-left-leave-active,
.slide-horizontal-right-enter-active,
.slide-horizontal-right-leave-active {
  transition: all 0.3s ease;
}

.slide-horizontal-left-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-horizontal-left-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.slide-horizontal-right-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-horizontal-right-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.tag-input-container {
  display: inline-flex;
  align-items: center;
  margin: 4px;
}

.tag-input {
  background-color: #f0f0f0;
  border: none;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  min-width: 100px;
  outline: none;
}

.tag-input:focus {
  background-color: #e0e0e0;
}
</style>
